import React, { useContext, useEffect, useRef, useState } from 'react'
import anime from 'animejs'
import delay from 'delay'
import clsx from 'clsx'
import { Button, CircularProgress, Paper, Snackbar, useTheme } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ShieldIcon from '@mui/icons-material/Shield'

import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'
import ConfirmationModal from '../ConfirmationModal'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import MetaWabaSnapshot from './MetaWabaSnapshot'
import { APP_VERSION } from '../../services/appVersion'

const REBOOT_COOLDOWN_MS = 5 * 60 * 1000

function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Versão compacta do diagrama de conexão, embutida no painel Meta (entre os cards e os
// próximos passos). Sem refs/slide próprio: a entrada é feita pela animação em cascata do
// snapshot (data-anim-card). O badge "NOVO" deu lugar à versão atual do painel.
function ConnectionModuleCompact({ theme, server }) {
  return (
    <Paper elevation={2}>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <ShieldIcon style={{ color: '#00ccffff', fontSize: 20 }} />
          <Text className="text-xs font-bold uppercase tracking-wider opacity-70 flex-1">
            Status da conexão
          </Text>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold"
            style={{ backgroundColor: '#00ccff1F', color: '#0094c6' }}
          >
            <span className="w-[8px] h-[8px] rounded-full mr-1.5 bg-[#00ccffff]" />
            Conectado
          </span>
        </div>

        <div className="flex items-center justify-between gap-3 md:gap-5">
          {/* telefone */}
          <div
            className={clsx('relative w-[60px] md:w-[84px] h-[96px] md:h-[132px] rounded-md flex-shrink-0', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#ccccccff]': !theme.isDark,
            })}
          >
            <div
              className={clsx('absolute left-1/2 -translate-x-1/2 top-[7%] w-[30%] h-[5px] rounded-md bg-white', {
                'opacity-[0.1]': theme.isDark,
                'opacity-[0.7]': !theme.isDark,
              })}
            />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[34px] md:w-[48px] h-[34px] md:h-[48px] bg-[#00ccffff] rounded-full flex justify-center items-center">
              <CheckIcon className="text-white" style={{ fontSize: 28 }} />
            </div>
          </div>

          {/* meio: logo Meta + versão + pipe animada */}
          <div className="relative flex-1 flex flex-col items-center">
            <img src={theme.isDark ? metaWhite : metaPrimary} className="w-[96px] md:w-[120px]" alt="Meta" />
            <span
              style={{ backgroundColor: theme.palette.primary.main }}
              className="font-black px-1.5 text-white text-[10px] mt-1 tracking-wider"
            >
              v{APP_VERSION}
            </span>
            <div
              className={clsx('relative w-full h-[10px] overflow-hidden mt-3 rounded-sm', {
                'bg-[#202124ff]': theme.isDark,
                'bg-[#20212442]': !theme.isDark,
              })}
            >
              <div className="absolute w-[40%] h-full animate-left-to-right animate-infinite animate-duration-[0.6s] bg-[#00ccffff]" />
            </div>
          </div>

          {/* servidor */}
          <div
            className={clsx('relative w-[60px] md:w-[84px] h-[96px] md:h-[132px] rounded-md flex-shrink-0', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#ccccccff]': !theme.isDark,
            })}
          >
            <div className="p-2 md:p-3 flex flex-col h-full">
              <div className="grid grid-cols-12 gap-[2px]">
                <div
                  className={clsx('col-span-10 h-[12px] md:h-[18px] rounded-sm', {
                    'bg-white opacity-[0.05]': theme.isDark,
                    'bg-gray-800 opacity-[0.2]': !theme.isDark,
                  })}
                />
                <div className="col-span-2 grid grid-cols-1 gap-[2px] h-[12px] md:h-[18px]">
                  <div className="bg-[#00ccffff] rounded-sm" />
                  <div className="bg-[#00ccffff] rounded-sm" />
                </div>
              </div>
              <div className="my-auto text-center">
                <Text
                  className={clsx('text-[9px] md:text-[11px] font-black leading-tight', {
                    'text-white opacity-[0.5]': theme.isDark,
                    'text-black opacity-[0.5]': !theme.isDark,
                  })}
                >
                  SERVIDOR HS
                </Text>
                <Text
                  className={clsx('text-base md:text-xl font-black leading-none', {
                    'text-white': theme.isDark,
                    'text-black opacity-[0.6]': !theme.isDark,
                  })}
                >
                  {server}
                </Text>
              </div>
              <div className="flex justify-end space-x-1">
                <div className="w-[6px] h-[6px] bg-emerald-600 rounded-full" />
                <div className="w-[6px] h-[6px] bg-emerald-600 rounded-full" />
                <div className="w-[6px] h-[6px] bg-emerald-600 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Paper>
  )
}

function DeviceConnected({ onDisconnect, lastInstanceStartDate }) {
  const API_VERSIONS_NEED_QRCODE = ['hiperapi-v1', 'hiperapi-v2']
  const API_VERSIONS_OFICIAL = ['hiperapi-v3-cloud-official']

  const { getCredentials, server, fetchData: getApiData, provider } = useContext(ApiDataWrapperContext)
  const { api: apiCreds, secret: secretCreds } = getCredentials()
  const isOfficial = API_VERSIONS_OFICIAL.includes(provider)
  const theme = useTheme()
  const phoneRef = useRef()
  const serverRef = useRef()
  const pipeRef = useRef()

  const [isRestarting, setIsRestarting] = useState(false)
  const [cooldownEndsAt, setCooldownEndsAt] = useState(null)
  const [cooldownRemaining, setCooldownRemaining] = useState(0)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [isOpenConfirmationModalDesconnectInstance, setIsOpenConfirmationModalDesconnectInstance] = useState(false)
  const [isOpenConfirmationModalRestartApi, setIsOpenConfirmationModalRestartApi] = useState(false)

  // Tick do contador regressivo (1s)
  useEffect(() => {
    if (!cooldownEndsAt) return
    const tick = () => {
      const remaining = cooldownEndsAt - Date.now()
      if (remaining <= 0) {
        setCooldownRemaining(0)
        setCooldownEndsAt(null)
      } else {
        setCooldownRemaining(remaining)
      }
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [cooldownEndsAt])

  // Inicializa cooldown a partir do lastInstanceStartDate vindo do pai
  useEffect(() => {
    if (!lastInstanceStartDate) return
    const elapsed = Date.now() - new Date(lastInstanceStartDate).getTime()
    const remaining = REBOOT_COOLDOWN_MS - elapsed
    if (remaining > 0) setCooldownEndsAt(Date.now() + remaining)
  }, [lastInstanceStartDate])

  const inCooldown = !!cooldownEndsAt && cooldownRemaining > 0
  const restartDisabled = isRestarting || inCooldown

  const handleAnimation = async () => {
    anime({
      targets: phoneRef.current,
      translateX: [250, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo'
    })
    anime({
      targets: serverRef.current,
      translateX: [-250, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo'
    })
    await delay(500)
    anime({
      targets: pipeRef.current,
      translateY: [10, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo'
    })
  }

  const handleExpireQrCode = async () => {
    try {
      const { api, secret } = getCredentials()
      await hipersendService.notificacoesInteligentes.expireQrCode(api, secret)
      onDisconnect()
    } catch (error) {
      console.error(error)
    }
  }

  const handleRestartApi = async () => {
    setIsOpenConfirmationModalRestartApi(false)
    setIsRestarting(true)
    try {
      const { api, secret } = getCredentials()
      await hipersendService.notificacoesInteligentes.restartApi(api, secret)
      setCooldownEndsAt(Date.now() + REBOOT_COOLDOWN_MS)
      setSnackbarMessage('API reiniciada com sucesso')
      await getApiData()
    } catch (error) {
      if (error?.response?.status === 429) {
        const retryAfterMs = error.response?.data?.retryAfterMs || REBOOT_COOLDOWN_MS
        setCooldownEndsAt(Date.now() + retryAfterMs)
        setSnackbarMessage(`Reinicialização bloqueada — aguarde ${formatCountdown(retryAfterMs)}`)
      } else {
        setSnackbarMessage('Falha ao reiniciar a API. Tente novamente.')
      }
    } finally {
      setIsRestarting(false)
    }
  }

  useEffect(() => {
    // O slide-in com refs só existe no diagrama standalone (modo QR Code). No modo oficial
    // o diagrama vai embutido no snapshot e entra pela animação em cascata de lá.
    if (!isOfficial) handleAnimation()
    getApiData()
  }, [])

  let buttonLabel = 'REINICIAR API'
  if (isRestarting) buttonLabel = 'REINICIANDO...'
  else if (inCooldown) buttonLabel = `AGUARDE ${formatCountdown(cooldownRemaining)}`

  return (
    <div>
      <ConfirmationModal
        open={isOpenConfirmationModalDesconnectInstance}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Desconectar
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para desconectar"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalDesconnectInstance(false)
        }}
        onConfirm={handleExpireQrCode}
      >
        <Text className="text-lg font-medium">Deseja realmente desconectar?</Text>
        <Text>Não se preocupe, caso desconectar você poderá reconectar em seguida caso necessário.</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalRestartApi}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Reiniciar api
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para reiniciar a api"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalRestartApi(false)
        }}
        onConfirm={handleRestartApi}
      >
        <Text>Deseja realmente reiniciar a api?</Text>
      </ConfirmationModal>
      <Snackbar
        open={!!snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
      {isOfficial && (
        <div className="mb-12">
          <MetaWabaSnapshot
            api={apiCreds}
            secret={secretCreds}
            connectionSlot={<ConnectionModuleCompact theme={theme} server={server} />}
          />
        </div>
      )}
      {/* Diagrama standalone (modo QR Code) — no modo oficial ele vai compacto dentro do snapshot */}
      {!isOfficial && (
      <div className="flex items-center justify-between">
        {/* cellphone */}
        <div ref={phoneRef} className="opacity-0">
          <div
            className={clsx('relative w-[120px] md:w-[240px] h-[190px] md:h-[370px] rounded-md md:rounded-lg', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#ccccccff]': !theme.isDark,
            })}
          >
            <div
              className={clsx('absolute left-1/2 -translate-x-1/2 top-[5%] w-[30%] h-[10px] rounded-md bg-white', {
                'opacity-[0.1]': theme.isDark,
                'opacity-[0.7]': !theme.isDark
              })}
            />
            <div className="absolute top-[12%] -translate-x-1/2 left-1/2 rounded-md md:rounded-lg h-[70%] w-[75%]">
              <div
                className={clsx('relative w-full h-full bg-white  rounded-md md:rounded-lg', {
                  'opacity-[0.1]': theme.isDark,
                  'opacity-[0.7]': !theme.isDark
                })}
              />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50px] md:w-[100px] h-[50px] md:h-[100px] bg-[#00ccffff] rounded-full flex justify-center items-center">
                <div className="hidden md:block">
                  <CheckIcon className="text-white" style={{ fontSize: 60 }} />
                </div>
                <div className="block md:hidden">
                  <CheckIcon className="text-white" style={{ fontSize: 30 }} />
                </div>
              </div>
            </div>
            <div
              className={clsx('absolute left-1/2 -translate-x-1/2 bottom-[5%] w-[20px] md:w-[30px] h-[20px] md:h-[30px] rounded-full bg-white', {
                'opacity-[0.1]': theme.isDark,
                'opacity-[0.7]': !theme.isDark
              })}
            />
          </div>
        </div>
        {/* connection */}
        <div ref={pipeRef} className="relative w-full opacity-0">
          <div
            className={clsx('relative w-full h-[20px] overflow-hidden', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#20212442]': !theme.isDark
            })}
          >
            <div className="absolute w-[40%] h-full animate-left-to-right animate-infinite animate-duration-[0.6s] bg-[#00ccffff]" />
          </div>
          <div className="absolute top-[120px] md:top-[60px] w-full">
            <div className="flex items-center justify-center space-x-2">
              <ShieldIcon style={{ color: '#00ccffff', fontSize: 35 }} />
              <p className="text-[#00ccffff] font-black text-xs md:text-3xl">CONECTADO</p>
            </div>
          </div>
        </div>
        {/* server */}
        <div ref={serverRef} className="opacity-0">
          <div
            className={clsx('relative w-[120px] md:w-[240px] h-[190px] md:h-[370px] rounded-md md:rounded-lg', {
              'bg-[#202124ff]': theme.isDark,
              'bg-[#ccccccff]': !theme.isDark
            })}
          >
            <div className="p-4 md:p-8 flex flex-col h-full">
              <div className="grid grid-cols-12 gap-[3px] md:gap-2">
                <div
                  className={clsx('col-span-10 h-[20px] md:h-[50px] rounded-md', {
                    'bg-white opacity-[0.05]': theme.isDark,
                    'bg-gray-800 opacity-[0.2]': !theme.isDark
                  })}
                >

                </div>
                <div className="col-span-2 grid grid-cols-1 gap-[3px] md:gap-2 h-[20px] md:h-[50px]">
                  <div className="bg-[#00ccffff] rounded-md"></div>
                  <div className="bg-[#00ccffff] rounded-md"></div>
                </div>
              </div>
              <div className="my-auto">
                <Text
                  className={clsx('text-xs md:text-2xl font-black text-center', {
                    'text-white opacity-[0.5]': theme.isDark,
                    'text-black opacity-[0.5]': !theme.isDark
                  })}
                >SERVIDOR HS</Text>
                <Text
                  className={clsx('md:text-4xl font-black text-center', {
                    'text-white': theme.isDark,
                    'text-black opacity-[0.5]': !theme.isDark
                  })}
                >{server}</Text>
              </div>
              <div className="flex justify-end space-x-1 md:space-x-2">
                <div className="w-[10px] md:w-[20px] h-[10px] md:h-[20px] bg-emerald-600 rounded-full" />
                <div className="w-[10px] md:w-[20px] h-[10px] md:h-[20px] bg-emerald-600 rounded-full" />
                <div className="w-[10px] md:w-[20px] h-[10px] md:h-[20px] bg-emerald-600 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
      <div className="mt-20 flex justify-center space-x-8">
        {
          API_VERSIONS_OFICIAL.includes(provider) &&
          <Button onClick={() => setIsOpenConfirmationModalDesconnectInstance(true)}>
            DESCONECTAR
          </Button>
        }
        {
          API_VERSIONS_NEED_QRCODE.includes(provider) &&
          <Button onClick={() => setIsOpenConfirmationModalDesconnectInstance(true)}>
            EXPIRAR QRCODE
          </Button>
        }
        <Button
          onClick={() => !restartDisabled && setIsOpenConfirmationModalRestartApi(true)}
          disabled={restartDisabled}
          startIcon={isRestarting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  )
}

export default DeviceConnected
